import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Phone, Shield, Flame } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import api from '@/lib/api';
import type { ApiResponse } from '@hirenfire/shared';

export function PhoneVerifyPage() {
  const navigate = useNavigate();

  const [step, setStep] = useState<'phone' | 'otp'>('phone');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSendOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await api.post<ApiResponse>('/auth/phone/send-otp', { phoneNumber });
      setStep('otp');
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data
          ?.error?.message ?? 'Failed to send OTP. Please try again.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await api.post<ApiResponse>('/auth/phone/verify-otp', {
        phoneNumber,
        code: otpCode,
      });
      navigate('/dashboard', { replace: true });
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data
          ?.error?.message ?? 'Invalid OTP. Please try again.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-dark-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Flame className="w-10 h-10 text-primary-500" />
            <span className="text-3xl font-bold gradient-text">Hirenfire</span>
          </div>
          <p className="text-gray-400">Verify your phone number</p>
        </div>

        <div className="card-dark p-8">
          {step === 'phone' ? (
            <form onSubmit={handleSendOtp} className="space-y-5">
              {error && (
                <div className="p-3 bg-error-500/10 border border-error-500/30 rounded-lg text-error-400 text-sm">
                  {error}
                </div>
              )}

              <div className="text-center mb-4">
                <div className="w-16 h-16 mx-auto mb-4 bg-primary-500/10 rounded-full flex items-center justify-center">
                  <Phone className="w-8 h-8 text-primary-400" />
                </div>
                <p className="text-gray-400 text-sm">
                  We need to verify your phone number to prevent fraud and ensure platform security.
                  VoIP numbers are not accepted.
                </p>
              </div>

              <Input
                label="Phone Number"
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                leftIcon={<Phone className="w-4 h-4" />}
                required
              />

              <Button
                type="submit"
                className="w-full"
                size="lg"
                isLoading={isLoading}
              >
                Send Verification Code
              </Button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOtp} className="space-y-5">
              {error && (
                <div className="p-3 bg-error-500/10 border border-error-500/30 rounded-lg text-error-400 text-sm">
                  {error}
                </div>
              )}

              <div className="text-center mb-4">
                <div className="w-16 h-16 mx-auto mb-4 bg-success-500/10 rounded-full flex items-center justify-center">
                  <Shield className="w-8 h-8 text-success-400" />
                </div>
                <p className="text-gray-400 text-sm">
                  Enter the 6-digit code sent to{' '}
                  <span className="text-white font-medium">{phoneNumber}</span>
                </p>
              </div>

              <Input
                label="Verification Code"
                type="text"
                placeholder="000000"
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                maxLength={6}
                className="text-center text-2xl tracking-[0.5em] font-mono"
                required
              />

              <Button
                type="submit"
                className="w-full"
                size="lg"
                isLoading={isLoading}
              >
                Verify
              </Button>

              <button
                type="button"
                onClick={() => {
                  setStep('phone');
                  setOtpCode('');
                  setError('');
                }}
                className="w-full text-sm text-gray-400 hover:text-white transition-colors"
              >
                Change phone number
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
